/**
 * useSessionWebSocket — Real-time WebSocket listeners for SessionDetailsModal.
 * Listens for 8 event types and invalidates the relevant react-query caches
 * so the modal stays in sync without manual refreshes.
 */
import { useEffect, useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { wsService } from '../../../../services/websocket';
import { SubSession } from '../../../../services/productionService';

interface UseSessionWebSocketParams {
  sessionNumber: string;
  subSessions: SubSession[];
  refetchHistory: () => void;
}

export function useSessionWebSocket({
  sessionNumber,
  subSessions,
  refetchHistory,
}: UseSessionWebSocketParams): void {
  const queryClient = useQueryClient();

  // Stable dependency: only changes when actual sub-session IDs change, not on every refetch
  const subSessionNumbers = useMemo(
    () => subSessions.map(ss => ss.sub_session_number).sort().join(','),
    [subSessions]
  );

  useEffect(() => {
    wsService.connect();

    // Handler for clock-in events - refresh history when someone clocks in to this session
    const handleWorkerClockedIn = (data: any) => {
      const isRelevant = subSessions.some(ss => ss.sub_session_number === data.sub_session_number);
      if (isRelevant || data.session_number === sessionNumber) {
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
      }
    };

    // Handler for clock-out events - refresh history when someone clocks out
    const handleWorkerClockedOut = (data: any) => {
      const isRelevant = subSessions.some(ss => ss.sub_session_number === data.sub_session_number);
      if (isRelevant || data.session_number === sessionNumber) {
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
      }
    };

    // Handler for box-packed events - refresh when boxes are packed for this session
    const handleBoxPacked = (data: any) => {
      if (String(data.session_number) === String(sessionNumber)) {
        refetchHistory();
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
      }
    };

    // Handler for box-cancelled events - refresh when boxes are cancelled for this session
    const handleBoxCancelled = (data: any) => {
      if (String(data.session_number) === String(sessionNumber)) {
        refetchHistory();
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
      }
    };

    // Handler for box-deleted events - refresh when pending boxes are deleted (print failed)
    const handleBoxDeleted = (data: any) => {
      if (String(data.session_number) === String(sessionNumber)) {
        refetchHistory();
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
      }
    };

    // Handler for pending-label-voided events - refresh when pending labels are voided by supervisor
    const handlePendingLabelVoided = (data: any) => {
      if (String(data.session_number) === String(sessionNumber)) {
        refetchHistory();
        queryClient.invalidateQueries(['session-history', sessionNumber]);
        queryClient.invalidateQueries(['session-details', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
      }
    };

    // Handler for general session updates
    const handleSessionUpdated = (data: any) => {
      if (data.session_number === sessionNumber) {
        queryClient.invalidateQueries(['session-details', sessionNumber]);
        queryClient.invalidateQueries(['sub-sessions', sessionNumber]);
        queryClient.invalidateQueries(['productionSessions']);
      }
    };

    // Handler for supervisor override events (weight mode changes, label reprints, etc.)
    const handleSupervisorOverride = (data: any) => {
      const isRelevant = subSessions.some(ss => ss.sub_session_number === data.sub_session_number) ||
                         String(data.session_number) === String(sessionNumber);
      if (isRelevant) {
        refetchHistory();
        queryClient.invalidateQueries(['session-history', sessionNumber]);
      }
    };

    wsService.on('worker-clocked-in', handleWorkerClockedIn);
    wsService.on('worker-clocked-out', handleWorkerClockedOut);
    wsService.on('box-packed', handleBoxPacked);
    wsService.on('box-cancelled', handleBoxCancelled);
    wsService.on('box-deleted', handleBoxDeleted);
    wsService.on('pending-label-voided', handlePendingLabelVoided);
    wsService.on('production-session-updated', handleSessionUpdated);
    wsService.on('supervisor-override', handleSupervisorOverride);

    return () => {
      wsService.off('worker-clocked-in', handleWorkerClockedIn);
      wsService.off('worker-clocked-out', handleWorkerClockedOut);
      wsService.off('box-packed', handleBoxPacked);
      wsService.off('box-cancelled', handleBoxCancelled);
      wsService.off('box-deleted', handleBoxDeleted);
      wsService.off('pending-label-voided', handlePendingLabelVoided);
      wsService.off('production-session-updated', handleSessionUpdated);
      wsService.off('supervisor-override', handleSupervisorOverride);
    };
  }, [sessionNumber, subSessionNumbers, queryClient, refetchHistory]);
}
